import { DataTestId, LocalizedCurrencySimpleValue, SimpleAmount, TextKey } from '@mews-distributor/common';
import { LocalizedText } from '@mews/framework';
import { Body, Highlight, Stack, Typography } from '@optimus-web/core';

interface TotalBarProps {
    hasNetPricing: boolean;
    renderRateGroupDescription?: () => React.ReactNode;
    showPropertyCurrency: boolean;
    totalPrice: SimpleAmount;
    totalPriceInPropertyCurrency: string;
    selectedCurrency: string;
    paymentGatewayCurrency: string | undefined;
    hotelCurrency: string;
    showSurchargingMessage: boolean;
    hasSurchargeService: boolean;
    surchargeFees: {
        visa: number;
        mastercard: number;
        amex: number;
    };
    isDccFlowEnabled: boolean;
}

export const TotalBar = ({
    hasNetPricing,
    renderRateGroupDescription,
    showPropertyCurrency,
    totalPrice,
    totalPriceInPropertyCurrency,
    selectedCurrency,
    paymentGatewayCurrency,
    hotelCurrency,
    showSurchargingMessage,
    hasSurchargeService,
    surchargeFees,
    isDccFlowEnabled,
    ...other
}: TotalBarProps) => (
    <Stack spacing="100" vertical {...other}>
        <Stack spacing="200" distribution="space-between">
            <Highlight size="medium" data-test-id={DataTestId.TotalBarTotal}>
                {showPropertyCurrency ? (
                    <LocalizedText textKey={TextKey.YouWillPayIn} substitutes={{ currency: hotelCurrency }} />
                ) : (
                    <LocalizedText textKey={TextKey.Total} />
                )}
            </Highlight>

            <Highlight size="medium" data-test-id={DataTestId.TotalBarTotalValue}>
                {showPropertyCurrency ? (
                    totalPriceInPropertyCurrency
                ) : (
                    <LocalizedCurrencySimpleValue value={totalPrice} />
                )}
            </Highlight>
        </Stack>
        {showPropertyCurrency ? (
            <Stack spacing="200" distribution="space-between">
                <Highlight size="small">
                    <LocalizedText textKey={TextKey.ApproximatePriceIn} substitutes={{ currency: selectedCurrency }} />
                </Highlight>
                <Highlight size="small">
                    <LocalizedCurrencySimpleValue value={totalPrice} />
                </Highlight>
            </Stack>
        ) : null}
        {hasNetPricing ? (
            <Stack alignment="end" data-test-id={DataTestId.TotalBarTaxIncluded}>
                <Typography textStyle="bodyMediumStrong" variant="secondary" as="div">
                    <LocalizedText textKey={TextKey.TaxIncluded} />
                </Typography>
            </Stack>
        ) : null}
        {renderRateGroupDescription ? (
            <Stack alignment="end" data-test-id={DataTestId.RateGroupDescription}>
                <Typography textStyle="bodyMediumStrong" variant="secondary" as="div">
                    {renderRateGroupDescription()}
                </Typography>
            </Stack>
        ) : null}

        {showSurchargingMessage && hasSurchargeService ? (
            <Body size="medium" variant="secondary" data-test-id={DataTestId.SurchargingMessage}>
                <LocalizedText
                    textKey={TextKey.SurchargeFeeMessage}
                    substitutes={{
                        visaFee: surchargeFees.visa,
                        mastercardFee: surchargeFees.mastercard,
                        amexFee: surchargeFees.amex,
                    }}
                />
            </Body>
        ) : null}
    </Stack>
);
